import React from 'react';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import {
  CALC_OUTCOME_TEST_ID,
  EmploymentType,
  RETIREMENT_CALCULATOR_NAMESPACE,
  SavingsProduct,
} from '../../tools/RetirementCalculator.const';
import { RetirementCalculatorOutcomePropsModel } from '../../tools/RetirementCalculator.model';
import { useRetirementChartSeries } from '../../tools/useRetirementChartSeries';
import { RetirementCalculatorChartCard } from '../RetirementCalculatorChartCard/RetirementCalculatorChartCard';
import { RetirementCalculatorPromoCard } from '../RetirementCalculatorPromoCard/RetirementCalculatorPromoCard';
import { RetirementCalculatorOutcomeResultCard } from './RetirementCalculatorOutcomeResultCard';

import useStyles from './RetirementCalculatorOutcome.style';

export const RetirementCalculatorOutcome: React.FC<RetirementCalculatorOutcomePropsModel> = ({
  projection,
  promotedProduct,
  hasIke,
  payoutYears,
  monthlyInvestment,
  maxMonthlyInvestment,
  onMonthlyInvestmentChange,
  employmentType,
  promoTitle,
  promoDescription,
  learnMoreLabel,
  learnMoreUrl,
  onDownloadSummary,
  className,
}) => {
  const c = useStyles({});
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });
  const { series, scaleTicks, barsCount } = useRetirementChartSeries(projection, promotedProduct);

  const productName = t(`savings.products.${promotedProduct}`);

  const ikzeWithoutIkeDescriptionKey =
    employmentType === EmploymentType.BUSINESS ? 'ikze-business' : 'ikze-without-ike';
  const promoDescriptionKey =
    promotedProduct !== SavingsProduct.IKZE || hasIke !== false
      ? promotedProduct
      : ikzeWithoutIkeDescriptionKey;

  return (
    <div className={clsx(c.root, className)}>
      <RetirementCalculatorPromoCard
        variant="wide"
        testId={`${CALC_OUTCOME_TEST_ID}-promo`}
        descriptionClassName={c.promoDescription}
        badge={t('outcome.promo.badge')}
        title={promoTitle ?? t('outcome.promo.title')}
        description={
          promoDescription ?? (
            <Trans
              t={t}
              i18nKey={`outcome.promo.description.${promoDescriptionKey}`}
              values={{
                employmentType: employmentType
                  ? t(`outcome.promo.employment-type.${employmentType}`)
                  : '',
              }}
              components={{ accent: <strong />, br: <br /> }}
            />
          )
        }
      />

      <RetirementCalculatorOutcomeResultCard
        projection={projection}
        promotedProduct={promotedProduct}
        payoutYears={payoutYears}
        monthlyInvestment={monthlyInvestment}
        maxMonthlyInvestment={maxMonthlyInvestment}
        onMonthlyInvestmentChange={onMonthlyInvestmentChange}
        learnMoreLabel={learnMoreLabel}
        learnMoreUrl={learnMoreUrl}
        onDownloadSummary={onDownloadSummary}
      />

      <RetirementCalculatorChartCard
        series={series}
        scaleTicks={scaleTicks}
        barsCount={barsCount}
        barCaptions={[
          t('result.bar.zus-only'),
          t('result.bar.with-savings'),
          t('outcome.bar.with-product', { product: productName, count: payoutYears }),
        ]}
        highlightedBar={2}
        description={t('outcome.chart-description', { product: productName })}
        testId={`${CALC_OUTCOME_TEST_ID}-chart`}
      />
    </div>
  );
};
